import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * LR HUB inline editor for an existing post or comment.
 *
 * Saving does NOT write directly — the parent routes the new text back through
 * `moderate-hub-post` / `moderate-hub-comment`, so edited text is re-moderated
 * (and ownership is re-verified server-side) before it lands.
 */

export function HubInlineEditor({
  initial,
  max,
  saving,
  label = "Save",
  onCancel,
  onSave,
}: {
  initial: string;
  max: number;
  saving?: boolean;
  label?: string;
  onCancel: () => void;
  onSave: (body: string) => void;
}) {
  const [body, setBody] = useState(initial);
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  const len = body.trim().length;
  const nearLimit = len >= max - 40;
  const atLimit = len >= max;
  const changed = body.trim() !== initial.trim();
  const canSave = len > 0 && changed && !saving;

  return (
    <div className="mt-3 rounded-lg border border-brand-teal/40 bg-background px-2.5 py-2">
      <textarea
        ref={ref}
        value={body}
        onChange={(e) => setBody(e.target.value.slice(0, max))}
        rows={3}
        className="w-full resize-y bg-transparent text-[12.5px] leading-relaxed text-foreground focus:outline-none"
      />
      <div className="mt-1 flex items-center justify-end gap-2">
        <span
          className={cn(
            "mr-auto text-[10px] tabular-nums",
            atLimit
              ? "font-bold text-danger"
              : nearLimit
                ? "font-semibold text-brand-amber"
                : "text-muted-foreground",
          )}
        >
          {len}/{max}
        </span>
        <button
          type="button"
          onClick={onCancel}
          className="text-[11px] font-semibold text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!canSave}
          onClick={() => onSave(body.trim())}
          className={cn(
            "inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[11.5px] font-bold transition-colors",
            canSave
              ? "bg-brand-teal-deep text-white hover:bg-brand-teal-deep/85"
              : "cursor-not-allowed bg-muted text-muted-foreground",
          )}
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
          {label}
        </button>
      </div>
    </div>
  );
}

export default HubInlineEditor;
